import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {T} from "@tolgee/react";
import useEmbeddedShows from "view/EmbeddedShows/helpers/useEmbeddedShows";
import {useParams} from "react-router-dom";
import useSearchParam from "hooks/useSearchParam";
import {AxiosError} from "axios";
import {apiV2} from "api/firebase";
import {getErrorMessage} from "tools/error";
import {completeGoogleSignIn} from "tools/auth/completeGoogleSignIn";
import {
  GoogleCallbackResult,
  GoogleCallbackStatus,
  GoogleSignInReason,
  parseGoogleCallbackResult,
  resolveGoogleErrorKey,
  startGoogleSignIn,
  stripGoogleCallbackFragment,
} from "tools/auth/googleSignIn";
import {
  CredentialsFormState,
  ExtendedShow,
  GoogleIdentity,
  GoogleTwoFactorState,
  MultiStepFormActions,
  MultiStepFormState,
  SignUpResult,
  StandDetailsFormState,
  SubmitExhibitorBody,
  ValidateExhibitorResponse,
} from "view/SignUp/SignUpToken/context/types";
import {
  clearSignUpForm,
  persistSignUpForm,
  readSignUpForm,
} from "view/SignUp/SignUpToken/context/persistence";
import EmailInUseByAnotherRole from "view/SignUp/SignUpToken/components/EmailInUseByAnotherRole";
import applyGoogleEmail from "view/SignUp/SignUpToken/utils/applyGoogleEmail";
import resumeCredentials from "view/SignUp/SignUpToken/utils/resumeCredentials";

const initialStandDetailsFormState: StandDetailsFormState = {
  firstName: "",
  lastName: "",
  buildType: "",
  companyAddress: "",
  companyPostcode: "",
  isIrregularSelected: false,
};

const initialCredentials: CredentialsFormState = {
  standNumber: "",
  email: "",
  companyName: "",
};

const FormStateContext = createContext<MultiStepFormState | null>(null);
const FormActionsContext = createContext<MultiStepFormActions | null>(null);

export const useFormStateContext = () => {
  const formContext = useContext(FormStateContext);
  if (!formContext) {
    throw new Error("useFormStateContext must be called from context provider");
  }
  return formContext;
};
export const useFormActionsContext = () => {
  const formContext = useContext(FormActionsContext);
  if (!formContext) {
    throw new Error(
      "useFormActionsContext must be called from context provider"
    );
  }
  return formContext;
};
// api-v2 rejects the embed token / show list with these statuses (the Firebase
// callable used to answer `permission-denied` / `not-found`); their message is
// meant for the visitor ("This event is no longer online").
const ERROR_STATUSES = [403, 404];

/**
 * The message, if any, to greet the person with on the way back from Google.
 *
 * Backing out of the Google window says nothing: the person chose to come
 * back, the form is exactly as they left it, and telling them what they just
 * did would read as a failure they have to act on.
 *
 * `Success` and `TwoFactor` cannot arrive here — this page asks for identity
 * mode, which never starts a session — but the union includes them, and
 * silence would leave such a case looking like a no-op.
 */
const resolveInitialGoogleError = (
  callback: GoogleCallbackResult | null
): React.ReactNode => {
  if (callback?.status === GoogleCallbackStatus.Identity) {
    // Connected, but the address can never be an exhibitor's. Said now rather
    // than after Next.
    return callback.isEmailInUseByAnotherRole ? (
      <EmailInUseByAnotherRole email={callback.email} isFromGoogle />
    ) : null;
  }

  if (!callback) {
    return null;
  }

  if (
    callback.status === GoogleCallbackStatus.Success ||
    callback.status === GoogleCallbackStatus.TwoFactor
  ) {
    return <T keyName={resolveGoogleErrorKey(null)} />;
  }

  if (callback.reason === GoogleSignInReason.AccessDenied) return null;

  return <T keyName={resolveGoogleErrorKey(callback.reason)} />;
};

// The whitelisted route pattern, not this page's URL: the backend compares
// return paths by exact match, and the concrete `uid` travels in the query.
const GOOGLE_RETURN_PATH = "/sign-up/:uid";

const MultiStepFormProvider = ({children}: {children: ReactNode}) => {
  const {uid: token} = useParams();
  const showId = useSearchParam("showId");

  // Read during the first render, not in an effect: each step builds its
  // react-hook-form with `defaultValues` taken from this context, and those
  // are captured on mount. Restoring afterwards would leave the fields empty.
  //
  // Both reads are pure. `React.StrictMode` (see src/index.tsx) invokes render
  // twice in development, so an initialiser that also consumed what it read
  // would hand the second invocation nothing and lose the identity.
  const [returnedFromGoogle] = useState(() => {
    const callback = parseGoogleCallbackResult();
    const identity: GoogleIdentity | null =
      callback?.status === GoogleCallbackStatus.Identity
        ? {identityToken: callback.identityToken, email: callback.email}
        : null;

    const form = token ? readSignUpForm(token, showId) : null;
    const resumed = form ? resumeCredentials(form, identity) : null;

    return {
      callback,
      identity: resumed ? resumed.identity : identity,
      form,
      resumed,
    };
  });

  // Consumed once the values above are safely in state: the fragment holds a
  // one-time token, and the draft belongs to the trip that just ended.
  useEffect(() => {
    if (returnedFromGoogle.callback) stripGoogleCallbackFragment();
    if (returnedFromGoogle.form) clearSignUpForm();
  }, [returnedFromGoogle]);

  // The draft keeps the typed address only for a trip that ends without an
  // account. Once Google returns one, the typed address is dropped here and
  // never reaches a later step — the verified one is what the API will use.
  const [credentials, setCredentials] = useState<CredentialsFormState>(
    () =>
      returnedFromGoogle.resumed?.credentials ??
      applyGoogleEmail(initialCredentials, returnedFromGoogle.identity)
  );
  const [standDetails, setStandDetails] = useState<StandDetailsFormState>(
    () => returnedFromGoogle.form?.standDetails ?? initialStandDetailsFormState
  );
  const [activeStep, setActiveStep] = useState<number>(
    () => returnedFromGoogle.form?.activeStep ?? 0
  );
  const [signUpResult, setSignUpResult] = useState<SignUpResult>(null);
  const [googleIdentity, setGoogleIdentity] = useState<GoogleIdentity | null>(
    returnedFromGoogle.identity
  );
  const [
    googleTwoFactor,
    setGoogleTwoFactor,
  ] = useState<GoogleTwoFactorState | null>(null);
  // A restored draft leaves the form and `credentials` identical, so the step
  // would take the "nothing changed" path even for values never validated —
  // see `resumeCredentials` for when the restored step must validate again.
  const [mustValidateCredentials, setMustValidateCredentials] = useState(
    () => returnedFromGoogle.resumed?.mustValidate ?? false
  );
  const [isGoogleBusy, setIsGoogleBusy] = useState(false);
  const [googleError, setGoogleError] = useState<React.ReactNode>(() =>
    resolveInitialGoogleError(returnedFromGoogle.callback)
  );
  // The same refusal marks the Email field, which keeps Next disabled.
  const [isEmailInUseByAnotherRole] = useState(
    () =>
      returnedFromGoogle.callback?.status === GoogleCallbackStatus.Identity &&
      returnedFromGoogle.callback.isEmailInUseByAnotherRole
  );

  // Back from Google can also restore this page from the back/forward cache,
  // frozen mid-redirect: the form is intact, but the button would spin forever.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;

      clearSignUpForm();
      setIsGoogleBusy(false);
    };

    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const [{shows}, isShowLoading, refreshShows] = useEmbeddedShows<ExtendedShow>(
    token,
    showId
  );
  const [show] = shows;

  const [
    validationResult,
    setValidationResult,
  ] = useState<ValidateExhibitorResponse | null>(
    () => returnedFromGoogle.resumed?.validationResult ?? null
  );
  const [submitError, setSubmitError] = useState<string | null>(null);

  const prevStep = useCallback(() => {
    setActiveStep((prev) => (prev === 0 ? prev : prev - 1));
  }, []);
  const nextStep = useCallback(() => {
    setActiveStep((prev) => prev + 1);
  }, []);

  const credentialsDraftReader = useRef<(() => CredentialsFormState) | null>(
    null
  );
  const setCredentialsDraftReader = useCallback(
    (read: (() => CredentialsFormState) | null) => {
      credentialsDraftReader.current = read;
    },
    []
  );

  // What the first step last validated. `credentials` is, except right after a
  // restore that still owes a validation — then the earlier trip's is.
  const lastValidatedCredentials = mustValidateCredentials
    ? returnedFromGoogle.form?.validatedCredentials ?? initialCredentials
    : credentials;

  const startGoogleSignUp = async () => {
    if (!token) return;

    try {
      setGoogleError(null);
      setIsGoogleBusy(true);

      if (!(await refreshShows())) {
        setIsGoogleBusy(false);
        return;
      }

      // What is in the fields now, not the last validated `credentials`: the
      // person may type and press the button without ever pressing Next.
      persistSignUpForm({
        token,
        showId,
        credentials: credentialsDraftReader.current?.() ?? credentials,
        validatedCredentials: lastValidatedCredentials,
        validationResult,
        googleIdentity,
        standDetails,
        activeStep,
      });
      await startGoogleSignIn(GOOGLE_RETURN_PATH, {uid: token});
    } catch (e) {
      console.error("Error starting Google sign-up: ", e);
      clearSignUpForm();
      setGoogleError(<T keyName={resolveGoogleErrorKey(null)} />);
      setIsGoogleBusy(false);
    }
  };

  /**
   * Turns the identity into a session, once the account behind it exists.
   *
   * Called straight from the browser rather than through the cloud function
   * the rest of this flow uses: the session cookie has to be set on the origin
   * that will send it back, and the two-factor and trusted-device checks have
   * to see the real browser.
   */
  const completeGoogleSignUp = useCallback(async () => {
    if (!googleIdentity) return;

    setGoogleError(null);
    setIsGoogleBusy(true);

    try {
      const result = await apiV2.auth.logInWithGoogleIdentity({
        identityToken: googleIdentity.identityToken,
      });

      if ("twoFactorRequired" in result) {
        // The account exists and has a second factor switched on. Ask for it
        // here rather than sending the person to the login page to start over
        // — the same challenge component both invite pages use.
        setGoogleTwoFactor({
          pendingToken: result.pendingToken,
          method: result.method,
          methods: result.methods,
        });
        setIsGoogleBusy(false);
        return;
      }

      clearSignUpForm();
      await completeGoogleSignIn(result.customToken);
    } catch (e) {
      console.error("Error signing in with Google identity: ", e);
      setGoogleError(<T keyName={resolveGoogleErrorKey(null)} />);
      setIsGoogleBusy(false);
    }
  }, [googleIdentity]);

  const completeGoogleTwoFactor = async (customToken: string) => {
    clearSignUpForm();
    await completeGoogleSignIn(customToken);
  };

  const cancelGoogleTwoFactor = () => setGoogleTwoFactor(null);

  const submitExhibitor = async (formData: SubmitExhibitorBody) => {
    const requestShowId = formData.showId ?? show?.id;
    const requestToken = formData.token ?? token;
    if (!requestShowId || !requestToken) {
      console.error("Cannot validate exhibitor without a show id and token");
      return;
    }
    setSubmitError(null);
    const previousCompanyName = lastValidatedCredentials.companyName;
    setMustValidateCredentials(false);

    try {
      // With Google connected the API checks the address the token proves and
      // words its refusals for someone it can sign straight in.
      const response = await apiV2.embeddedShows.validateExhibitor({
        companyName: formData.companyName,
        standNumber: formData.standNumber,
        email: formData.email,
        showId: requestShowId,
        token: requestToken,
        identityToken: googleIdentity?.identityToken,
      });
      const responseData = response as ValidateExhibitorResponse;

      // The second step's details belong to the company, so they start over
      // only when it changes. Validating the same company again — after a trip
      // to Google, say — keeps what the person typed there.
      if (formData.companyName !== previousCompanyName) {
        setStandDetails({
          ...initialStandDetailsFormState,
          companyAddress:
            responseData?.exhibitorCompany?.companyInfo?.address || "",
          companyPostcode:
            responseData?.exhibitorCompany?.companyInfo?.postcode || "",
        });
      }
      setValidationResult(responseData);

      if (responseData.status === "success") {
        nextStep();
      }
    } catch (e) {
      const status = (e as AxiosError)?.response?.status;
      if (status && ERROR_STATUSES.includes(status)) {
        setValidationResult({status: "error", message: getErrorMessage(e)});
      } else {
        // 429 from the rate limiter, 400 from validation, 5xx, network: tell
        // the visitor and leave the form retryable.
        setValidationResult(null);
        setSubmitError(getErrorMessage(e));
      }
      console.error(`Error occurred submitting exhibitor: ${e}`);
    }
  };
  const resetValidationState = () => {
    setValidationResult(null);
    setSubmitError(null);
  };
  return (
    <FormStateContext.Provider
      value={{
        credentials,
        standDetails,
        activeStep,
        show,
        isShowLoading,
        validationResult,
        submitError,
        signUpResult,
        googleIdentity,
        googleTwoFactor,
        mustValidateCredentials,
        isGoogleBusy,
        googleError,
        isEmailInUseByAnotherRole,
      }}
    >
      <FormActionsContext.Provider
        value={{
          setCredentials,
          setStandDetails,
          nextStep,
          prevStep,
          submitExhibitor,
          resetValidationState,
          setSignUpResult,
          setCredentialsDraftReader,
          startGoogleSignUp,
          completeGoogleSignUp,
          completeGoogleTwoFactor,
          cancelGoogleTwoFactor,
        }}
      >
        {children}
      </FormActionsContext.Provider>
    </FormStateContext.Provider>
  );
};

export default MultiStepFormProvider;
